import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AdminDashboardLayout from 'pages/admin-dashboard-overview/components/AdminDashboardLayout';
import { supabase } from '../../lib/supabase';
import { Plus, Pencil, Trash2, MoveRight, Star, ChevronDown, ChevronRight, Check, X, Bell, CheckCircle, XCircle, RefreshCw } from 'lucide-react';

// ─── Confirmation Dialog ───────────────────────────────────────────────────────
const ConfirmDialog = ({ message, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
    <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
      <p className="text-gray-800 text-sm mb-5">{message}</p>
      <div className="flex gap-3 justify-end">
        <button
          onClick={onCancel}
          className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors"
        >
          Annulla
        </button>
        <button
          onClick={onConfirm}
          className="px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
        >
          Elimina
        </button>
      </div>
    </div>
  </div>
);

// ─── Inline editable text ──────────────────────────────────────────────────────
const InlineEdit = ({ value, onSave, onCancel }) => {
  const [text, setText] = useState(value);
  return (
    <div className="flex items-center gap-2 flex-1">
      <input
        autoFocus
        type="text"
        value={text}
        onChange={(e) => setText(e?.target?.value)}
        onKeyDown={(e) => {
          if (e?.key === 'Enter') onSave(text?.trim());
          if (e?.key === 'Escape') onCancel();
        }}
        className="flex-1 border border-[#32b3e5] rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#32b3e5]/40"
      />
      <button onClick={() => onSave(text?.trim())} className="text-green-600 hover:text-green-700">
        <Check size={16} />
      </button>
      <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
        <X size={16} />
      </button>
    </div>
  );
};

// ─── Move subcategory modal ────────────────────────────────────────────────────
const MoveModal = ({ subcategory, categories, onMove, onCancel }) => {
  const [target, setTarget] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-1">Sposta sottocategoria</h3>
        <p className="text-xs text-gray-500 mb-4">
          Sposta <strong>{subcategory?.name}</strong> in un'altra categoria
        </p>
        <select
          value={target}
          onChange={(e) => setTarget(e?.target?.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#32b3e5] mb-4"
        >
          <option value="">— Seleziona categoria —</option>
          {categories?.filter((c) => c !== subcategory?.category)?.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
        </select>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            Annulla
          </button>
          <button
            disabled={!target}
            onClick={() => onMove(subcategory, target)}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-[#013776] text-white hover:bg-[#012a5e] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Sposta
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Category Requests Sub-view ────────────────────────────────────────────────
const CategoryRequestsView = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingText, setUpdatingText] = useState(null); // text being updated

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase
      ?.from('category_requests')
      ?.select('id, requested_text, user_id, context, status, created_at')
      ?.order('created_at', { ascending: false });

    if (fetchError) {
      setError(fetchError?.message);
    } else {
      setRequests(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  // Group by requested_text, count occurrences, keep most recent row per group
  const grouped = useMemo(() => {
    const map = {};
    (requests || [])?.forEach((row) => {
      const key = row?.requested_text?.toLowerCase()?.trim();
      if (!map?.[key]) {
        map[key] = { text: row?.requested_text, count: 0, latestRow: row, rows: [] };
      }
      map[key].count += 1;
      map?.[key]?.rows?.push(row);
      // Keep the most recent (already sorted desc, so first encountered is latest)
      if (new Date(row?.created_at) > new Date(map[key].latestRow?.created_at)) {
        map[key].latestRow = row;
      }
    });
    // Sort groups by most recent first
    return Object.values(map)?.sort(
      (a, b) => new Date(b?.latestRow?.created_at) - new Date(a?.latestRow?.created_at)
    );
  }, [requests]);

  // Mark all rows with this text as a given status
  const handleMarkAll = async (text, newStatus) => {
    setUpdatingText(text?.toLowerCase()?.trim());
    try {
      await supabase
        ?.from('category_requests')
        ?.update({ status: newStatus })
        ?.ilike('requested_text', text?.trim());
      await fetchRequests();
    } catch (_) {}
    setUpdatingText(null);
  };

  const pendingGroups = grouped?.filter(g =>
    g?.rows?.some(r => r?.status === 'pending')
  );
  const resolvedGroups = grouped?.filter(g =>
    g?.rows?.every(r => r?.status !== 'pending')
  );

  const statusBadge = (group) => {
    const statuses = [...new Set(group?.rows?.map(r => r?.status))];
    if (statuses?.includes('added')) return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
        <CheckCircle size={11} /> Aggiunta
      </span>
    );
    if (statuses?.includes('rejected')) return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
        <XCircle size={11} /> Rifiutata
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-full px-2 py-0.5">
        In attesa
      </span>
    );
  };

  const renderGroup = (group) => {
    const key = group?.text?.toLowerCase()?.trim();
    const isUpdating = updatingText === key;
    const hasPending = group?.rows?.some(r => r?.status === 'pending');

    return (
      <div key={key} className="flex items-center gap-4 px-4 py-3 bg-white rounded-xl border border-gray-200 shadow-sm hover:border-gray-300 transition-colors">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-800 text-sm truncate">{group?.text}</span>
            <span className="inline-flex items-center text-xs font-semibold text-[#013776] bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">
              {group?.count}×
            </span>
            {statusBadge(group)}
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            Ultima richiesta: {new Date(group?.latestRow?.created_at)?.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        {hasPending && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => handleMarkAll(group?.text, 'added')}
              disabled={isUpdating}
              title="Segna come aggiunta"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isUpdating ? <RefreshCw size={12} className="animate-spin" /> : <CheckCircle size={12} />}
              Aggiunta
            </button>
            <button
              onClick={() => handleMarkAll(group?.text, 'rejected')}
              disabled={isUpdating}
              title="Segna come rifiutata"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isUpdating ? <RefreshCw size={12} className="animate-spin" /> : <XCircle size={12} />}
              Rifiuta
            </button>
          </div>
        )}
      </div>
    );
  };

  if (loading) return (
    <div className="mt-8 flex justify-center">
      <div className="w-8 h-8 border-4 border-[#013776] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="mt-6 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">{error}</div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          {pendingGroups?.length > 0
            ? `${pendingGroups?.length} categorie in attesa di revisione`
            : 'Nessuna richiesta in attesa'}
        </p>
        <button
          onClick={fetchRequests}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
        >
          <RefreshCw size={13} />
          Aggiorna
        </button>
      </div>

      {grouped?.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <Bell size={32} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">Nessuna richiesta di categoria ricevuta.</p>
        </div>
      )}

      {pendingGroups?.length > 0 && (
        <div className="space-y-2 mb-6">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">In attesa</h3>
          {pendingGroups?.map(renderGroup)}
        </div>
      )}

      {resolvedGroups?.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Gestite</h3>
          {resolvedGroups?.map(renderGroup)}
        </div>
      )}
    </div>
  );
};

// ─── Main page ─────────────────────────────────────────────────────────────────
const AdminCategorie = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tab state: 'categorie' | 'richieste'
  const [activeTab, setActiveTab] = useState('categorie');

  // UI state
  const [collapsed, setCollapsed] = useState({});
  const [editingId, setEditingId] = useState(null); // row id being renamed
  const [confirm, setConfirm] = useState(null);     // { message, onConfirm }
  const [moveTarget, setMoveTarget] = useState(null); // row being moved

  // Add category form
  const [newCatName, setNewCatName] = useState('');
  const [addingCat, setAddingCat] = useState(false);
  const [addCatError, setAddCatError] = useState(null);

  // Add subcategory form (per category)
  const [addingSubFor, setAddingSubFor] = useState(null); // category name
  const [newSubName, setNewSubName] = useState('');
  const [addSubError, setAddSubError] = useState(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase
      ?.from('professions')
      ?.select('id, name, category, is_special')
      ?.order('category', { ascending: true })
      ?.order('name', { ascending: true });

    if (fetchError) {
      setError(fetchError?.message);
    } else {
      setRows(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // ── Derived: group by category ─────────────────────────────────────────────
  // A "category row" is one where name === category (or no subcategories exist yet)
  // We group ALL rows by their `category` field.
  const grouped = React.useMemo(() => {
    const map = {};
    rows?.forEach((row) => {
      const cat = row?.category || row?.name;
      if (!map?.[cat]) map[cat] = { categoryRow: null, subcategories: [], is_special: false };
      // The row that represents the category itself (name === category)
      if (row?.name === row?.category || row?.category === null) {
        map[cat].categoryRow = row;
        map[cat].is_special = row?.is_special;
      } else {
        map?.[cat]?.subcategories?.push(row);
      }
    });
    // Also handle rows where category is set but no "header" row exists
    rows?.forEach((row) => {
      const cat = row?.category;
      if (cat && !map?.[cat]) {
        map[cat] = { categoryRow: null, subcategories: [row], is_special: false };
      }
    });
    return map;
  }, [rows]);

  const allCategories = Object.keys(grouped)?.sort();
  const specialCats = allCategories?.filter((c) => grouped?.[c]?.is_special);
  const normalCats = allCategories?.filter((c) => !grouped?.[c]?.is_special);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const toggleCollapse = (cat) =>
    setCollapsed((prev) => ({ ...prev, [cat]: !prev?.[cat] }));

  const askConfirm = (message, onConfirm) => setConfirm({ message, onConfirm });

  // ── CRUD ───────────────────────────────────────────────────────────────────

  // Add category: insert a row where name = category = newCatName
  const handleAddCategory = async () => {
    const name = newCatName?.trim();
    if (!name) return;
    setAddingCat(true);
    setAddCatError(null);
    const { data: { user } } = await supabase?.auth?.getUser();
    const { error: insertError } = await supabase?.from('professions')?.insert({ name, category: name, is_special: false });
    if (insertError) {
      setAddCatError(insertError?.message);
    } else {
      setNewCatName('');
      await fetchRows();
    }
    setAddingCat(false);
  };

  // Add subcategory: insert row with name = newSubName, category = addingSubFor
  const handleAddSubcategory = async () => {
    const name = newSubName?.trim();
    if (!name || !addingSubFor) return;
    setAddSubError(null);
    const { error: insertError } = await supabase?.from('professions')?.insert({ name, category: addingSubFor, is_special: false });
    if (insertError) {
      setAddSubError(insertError?.message);
    } else {
      setNewSubName('');
      setAddingSubFor(null);
      await fetchRows();
    }
  };

  // Rename a row (category header or subcategory)
  const handleRename = async (row, newName) => {
    if (!newName || newName === row?.name) { setEditingId(null); return; }
    const updates = { name: newName };
    // If renaming a category header, also update all subcategories' category field
    const isCategoryHeader = row?.name === row?.category;
    if (isCategoryHeader) {
      updates.category = newName;
      // Update all subcategories in this category
      await supabase?.from('professions')?.update({ category: newName })?.eq('category', row?.name)?.neq('name', row?.name); // don't double-update the header
    }
    await supabase?.from('professions')?.update(updates)?.eq('id', row?.id);
    setEditingId(null);
    await fetchRows();
  };

  // Delete a row
  const handleDelete = (row) => {
    const isCategoryHeader = row?.name === row?.category;
    const subcats = grouped?.[row?.category]?.subcategories || [];
    const message = isCategoryHeader
      ? subcats?.length > 0
        ? `Eliminare la categoria "${row?.name}" e tutte le sue ${subcats?.length} sottocategorie?`
        : `Eliminare la categoria "${row?.name}"?` : `Eliminare la sottocategoria"${row?.name}"?`;

    askConfirm(message, async () => {
      setConfirm(null);
      if (isCategoryHeader && subcats?.length > 0) {
        // Delete all subcategories first
        await supabase?.from('professions')?.delete()?.eq('category', row?.name)?.neq('name', row?.name);
      }
      await supabase?.from('professions')?.delete()?.eq('id', row?.id);
      await fetchRows();
    });
  };

  // Move subcategory to another category
  const handleMove = async (row, targetCategory) => {
    setMoveTarget(null);
    await supabase?.from('professions')?.update({ category: targetCategory })?.eq('id', row?.id);
    await fetchRows();
  };

  // Toggle is_special on a category header row
  const handleToggleSpecial = async (row) => {
    await supabase?.from('professions')?.update({ is_special: !row?.is_special })?.eq('id', row?.id);
    await fetchRows();
  };

  // ── Render helpers ─────────────────────────────────────────────────────────
  const renderCategoryBlock = (catName) => {
    const group = grouped?.[catName];
    if (!group) return null;
    const catRow = group?.categoryRow;
    const subs = group?.subcategories;
    const isOpen = !collapsed?.[catName];
    const isSpecial = group?.is_special;

    return (
      <div
        key={catName}
        className={`bg-white rounded-xl border ${isSpecial ? 'border-yellow-300' : 'border-gray-200'} shadow-sm overflow-hidden`}
      >
        {/* Category header row */}
        <div className={`flex items-center gap-3 px-4 py-3 ${isSpecial ? 'bg-yellow-50' : 'bg-gray-50'} border-b ${isSpecial ? 'border-yellow-200' : 'border-gray-200'}`}>
          <button
            onClick={() => toggleCollapse(catName)}
            className="text-gray-500 hover:text-gray-700 flex-shrink-0"
          >
            {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>

          {editingId === catRow?.id ? (
            <InlineEdit
              value={catName}
              onSave={(val) => handleRename(catRow, val)}
              onCancel={() => setEditingId(null)}
            />
          ) : (
            <span className="flex-1 font-semibold text-gray-800 text-sm">
              {catName}
              {isSpecial && (
                <span className="ml-2 inline-flex items-center gap-1 text-xs text-yellow-700 bg-yellow-100 border border-yellow-200 rounded-full px-2 py-0.5">
                  <Star size={11} fill="currentColor" /> Speciale
                </span>
              )}
              <span className="ml-2 text-xs text-gray-400 font-normal">({subs?.length} sottocategorie)</span>
            </span>
          )}

          {/* Category actions */}
          {editingId !== catRow?.id && catRow && (
            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Toggle special */}
              <button
                onClick={() => handleToggleSpecial(catRow)}
                title={isSpecial ? 'Rimuovi speciale' : 'Segna come speciale'}
                className={`p-1.5 rounded-lg transition-colors ${isSpecial ? 'text-yellow-600 hover:bg-yellow-100' : 'text-gray-400 hover:bg-gray-100 hover:text-yellow-500'}`}
              >
                <Star size={15} fill={isSpecial ? 'currentColor' : 'none'} />
              </button>
              {/* Add subcategory */}
              <button
                onClick={() => { setAddingSubFor(catName); setNewSubName(''); setAddSubError(null); }}
                title="Aggiungi sottocategoria"
                className="p-1.5 rounded-lg text-gray-400 hover:bg-blue-50 hover:text-[#013776] transition-colors"
              >
                <Plus size={15} />
              </button>
              {/* Rename */}
              <button
                onClick={() => setEditingId(catRow?.id)}
                title="Rinomina categoria"
                className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
              >
                <Pencil size={15} />
              </button>
              {/* Delete */}
              <button
                onClick={() => handleDelete(catRow)}
                title="Elimina categoria"
                className="p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
              >
                <Trash2 size={15} />
              </button>
            </div>
          )}
        </div>
        {/* Subcategories list */}
        {isOpen && (
          <div className="divide-y divide-gray-100">
            {subs?.length === 0 && addingSubFor !== catName && (
              <p className="px-6 py-3 text-xs text-gray-400 italic">Nessuna sottocategoria</p>
            )}
            {subs?.map((sub) => (
              <div key={sub?.id} className="flex items-center gap-3 px-6 py-2.5 hover:bg-gray-50 group">
                {editingId === sub?.id ? (
                  <InlineEdit
                    value={sub?.name}
                    onSave={(val) => handleRename(sub, val)}
                    onCancel={() => setEditingId(null)}
                  />
                ) : (
                  <>
                    <span className="flex-1 text-sm text-gray-700">{sub?.name}</span>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {/* Move */}
                      <button
                        onClick={() => setMoveTarget(sub)}
                        title="Sposta in altra categoria"
                        className="p-1.5 rounded-lg text-gray-400 hover:bg-blue-50 hover:text-[#013776] transition-colors"
                      >
                        <MoveRight size={14} />
                      </button>
                      {/* Rename */}
                      <button
                        onClick={() => setEditingId(sub?.id)}
                        title="Rinomina"
                        className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                      >
                        <Pencil size={14} />
                      </button>
                      {/* Delete */}
                      <button
                        onClick={() => handleDelete(sub)}
                        title="Elimina"
                        className="p-1.5 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}

            {/* Add subcategory inline form */}
            {addingSubFor === catName && (
              <div className="flex items-center gap-2 px-6 py-2.5 bg-blue-50 border-t border-blue-100">
                <input
                  autoFocus
                  type="text"
                  value={newSubName}
                  onChange={(e) => setNewSubName(e?.target?.value)}
                  onKeyDown={(e) => {
                    if (e?.key === 'Enter') handleAddSubcategory();
                    if (e?.key === 'Escape') { setAddingSubFor(null); setAddSubError(null); }
                  }}
                  placeholder="Nome sottocategoria..."
                  className="flex-1 border border-[#32b3e5] rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#32b3e5]/40 bg-white"
                />
                <button
                  onClick={handleAddSubcategory}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium bg-[#013776] text-white hover:bg-[#012a5e] transition-colors"
                >
                  Aggiungi
                </button>
                <button
                  onClick={() => { setAddingSubFor(null); setAddSubError(null); }}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
                {addSubError && (
                  <span className="text-xs text-red-600">{addSubError}</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <AdminDashboardLayout>
      <div>
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Categorie</h1>
            <p className="mt-1 text-gray-500 text-sm">Gestione categorie e sottocategorie dei professionisti</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-5 flex gap-1 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('categorie')}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'categorie' ?'bg-white border border-b-white border-gray-200 text-[#013776] -mb-px' :'text-gray-500 hover:text-gray-700'
            }`}
          >
            Categorie
          </button>
          <button
            onClick={() => setActiveTab('richieste')}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'richieste' ?'bg-white border border-b-white border-gray-200 text-[#013776] -mb-px' :'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Bell size={14} />
            Richieste categorie mancanti
          </button>
        </div>

        {/* Tab: Categorie */}
        {activeTab === 'categorie' && (
          <div className="mt-5">
            {/* Add new category */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Aggiungi nuova categoria</h2>
              <div className="flex gap-3 items-center">
                <input
                  type="text"
                  value={newCatName}
                  onChange={(e) => { setNewCatName(e?.target?.value); setAddCatError(null); }}
                  onKeyDown={(e) => { if (e?.key === 'Enter') handleAddCategory(); }}
                  placeholder="Nome categoria..."
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#32b3e5] focus:border-transparent"
                />
                <button
                  onClick={handleAddCategory}
                  disabled={addingCat || !newCatName?.trim()}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[#013776] text-white hover:bg-[#012a5e] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <Plus size={16} />
                  {addingCat ? 'Aggiunta...' : 'Aggiungi'}
                </button>
              </div>
              {addCatError && (
                <p className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{addCatError}</p>
              )}
            </div>

            {/* Loading / Error */}
            {loading && (
              <div className="mt-8 flex justify-center">
                <div className="w-8 h-8 border-4 border-[#013776] border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            {error && (
              <div className="mt-6 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">{error}</div>
            )}

            {!loading && !error && (
              <>
                {/* Special categories */}
                {specialCats?.length > 0 && (
                  <div className="mt-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Star size={16} className="text-yellow-500" fill="currentColor" />
                      <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Categorie speciali</h2>
                    </div>
                    <div className="space-y-3">
                      {specialCats?.map(renderCategoryBlock)}
                    </div>
                  </div>
                )}

                {/* Normal categories */}
                <div className="mt-6">
                  {specialCats?.length > 0 && (
                    <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Categorie standard</h2>
                  )}
                  {normalCats?.length === 0 && specialCats?.length === 0 && (
                    <p className="text-sm text-gray-400 italic">Nessuna categoria trovata.</p>
                  )}
                  <div className="space-y-3">
                    {normalCats?.map(renderCategoryBlock)}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Tab: Richieste categorie mancanti */}
        {activeTab === 'richieste' && (
          <div className="mt-5">
            <CategoryRequestsView />
          </div>
        )}
      </div>
      {/* Confirmation dialog */}
      {confirm && (
        <ConfirmDialog
          message={confirm?.message}
          onConfirm={confirm?.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
      {/* Move modal */}
      {moveTarget && (
        <MoveModal
          subcategory={moveTarget}
          categories={allCategories}
          onMove={handleMove}
          onCancel={() => setMoveTarget(null)}
        />
      )}
    </AdminDashboardLayout>
  );
};

export default AdminCategorie;
